import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { markRegistrationCompleted } from "@/lib/metrica";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, User as UserIcon, ShieldCheck, Building2, Mail, IdCard, Phone, X } from "lucide-react";
import { TERMS_BODY } from "@/pages/legal/terms";
import { KVKK_BODY } from "@/pages/legal/kvkk";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir hata oluştu, tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return (detail ?? []).map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login({ initialMode = "login" }) {
  const { user, login, checkAuth } = useAuth();
  const location = useLocation();
  // URL bazlı mod senkronizasyonu:
  //   /login    → "login"
  //   /register → "register"
  // Mode local state olarak tutulur; ekran içi geçişlerde ``navigate``
  // ile URL güncellenir → aynı Login componenti render edilir, tek
  // farkla ``initialMode`` prop'u.  Böylece mevcut form yapısı, state
  // yönetimi ve backend akışı DEĞİŞMEZ; yalnızca URL bağlantısı eklendi.
  const [mode, setMode] = useState(
    location.pathname === "/register" ? "register" : initialMode
  );
  useEffect(() => {
    if (location.pathname === "/register" && mode !== "register") setMode("register");
    else if (location.pathname === "/login" && mode !== "login") setMode("login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Kayıt kaynağı (UTM) yakalama — /register?utm_source=...&utm_medium=...
  // &utm_campaign=... parametreleri sayfa ilk açıldığında localStorage'a
  // yazılır ve kayıt tamamlanana kadar korunur.  Böylece kullanıcı
  // login ↔ register arasında geçse, sayfayı yenilese veya form birkaç
  // adım ilerlese bile UTM bilgisi kaybolmaz.  Payload register submit'te
  // eklenir; başarılı kayıt sonrası ``clearStoredUtm`` çağrılır.
  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search || "");
      const src = qs.get("utm_source");
      const med = qs.get("utm_medium");
      const cmp = qs.get("utm_campaign");
      if (src || med || cmp) {
        const payload = {
          utm_source:   src || null,
          utm_medium:   med || null,
          utm_campaign: cmp || null,
          captured_at: new Date().toISOString(),
        };
        window.localStorage.setItem("penz_register_utm", JSON.stringify(payload));
      }
    } catch (_) { /* localStorage guarded — SSR / privacy modda no-op */ }
  }, []);

  // login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // register state — hesap türü seçilene kadar kayıt olamaz
  const [regForm, setRegForm] = useState({
    billing_type: "",           // "individual" | "company"
    company_kind: "",           // "sahis_sirketi" | "limited" | "anonim" | "diger"
    first_name: "", last_name: "",   // şahıs
    full_name: "",                    // şirket yetkili ad soyad
    legal_name: "", national_id: "", tax_office: "", tax_number: "",
    billing_address: "", billing_city: "", billing_district: "",
    email: "", phone: "", password: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Consent state — kayıt formunun bir parçası ama regForm'dan ayrı tuttum
  // ki mevcut fatura form akışı ve validasyonları etkilenmesin.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [kvkkAcknowledged, setKvkkAcknowledged] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [openLegalModal, setOpenLegalModal] = useState(null); // "terms" | "kvkk" | null

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    // URL'i mod ile senkronize et → refresh ve deep-link doğru çalışır,
    // dönüşüm eventleri ve mevcut form akışı aynı kalır.
    const target = m === "register" ? "/register" : "/login";
    if (location.pathname !== target) navigate(target, { replace: false });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(username.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const onRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!regForm.billing_type) {
      setError("Lütfen hesap türünü seçin (Şahıs veya Şirket).");
      return;
    }
    if (regForm.billing_type === "company" && !regForm.company_kind) {
      setError("Lütfen şirket türünü seçin.");
      return;
    }
    // Şifre kuralı: en az 8, en az bir harf, en az bir rakam.
    if (
      regForm.password.length < 8 ||
      !/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(regForm.password) ||
      !/\d/.test(regForm.password)
    ) {
      setError("Şifre en az 8 karakter, bir harf ve bir rakam içermelidir.");
      return;
    }
    // Zorunlu onaylar
    if (!termsAccepted || !kvkkAcknowledged) {
      setError("Kayıt işlemini tamamlamak için Kullanım Koşulları'nı kabul etmeli ve KVKK Aydınlatma Metni'ni okuduğunuzu onaylamalısınız.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const isIndividual = regForm.billing_type === "individual";
      const isSahisSirketi =
        regForm.billing_type === "company" && regForm.company_kind === "sahis_sirketi";
      const payload = {
        billing_type: regForm.billing_type,
        company_kind: regForm.billing_type === "company" ? regForm.company_kind : null,
        first_name: isIndividual ? regForm.first_name.trim() : "",
        last_name:  isIndividual ? regForm.last_name.trim() : "",
        full_name:  regForm.billing_type === "company" ? regForm.full_name.trim() : "",
        email:      regForm.email.trim().toLowerCase(),
        phone:      regForm.phone.trim(),
        password:   regForm.password,
        legal_name: regForm.billing_type === "company" ? regForm.legal_name.trim() : "",
        national_id: (isIndividual || isSahisSirketi)
          ? regForm.national_id.replace(/\D/g, "")
          : "",
        tax_office:  regForm.billing_type === "company" ? regForm.tax_office.trim() : "",
        tax_number:  (regForm.billing_type === "company" && !isSahisSirketi)
          ? regForm.tax_number.replace(/\D/g, "")
          : "",
        billing_address: regForm.billing_type === "company" ? regForm.billing_address.trim() : "",
        billing_city:    regForm.billing_type === "company" ? regForm.billing_city.trim() : "",
        billing_district:regForm.billing_type === "company" ? regForm.billing_district.trim() : "",
        terms_accepted: termsAccepted,
        kvkk_acknowledged: kvkkAcknowledged,
        marketing_consent: marketingConsent,
      };
      // Kayıt kaynağı: localStorage'dan UTM'yi payload'a ekle.  Yoksa
      // sessizce atlanır (backend default "direct" uygular).
      try {
        const raw = window.localStorage.getItem("penz_register_utm");
        if (raw) {
          const utm = JSON.parse(raw);
          if (utm && typeof utm === "object") {
            payload.utm_source   = utm.utm_source   || null;
            payload.utm_medium   = utm.utm_medium   || null;
            payload.utm_campaign = utm.utm_campaign || null;
          }
        }
      } catch (_) { /* utm yükleme başarısız → direct kabul edilir */ }
      // Meta CAPI dedup — client + server aynı event_id'yi paylaşmalı.
      // Browser Pixel event'i backend response'u beklemeden ateşlenir;
      // backend aynı event_id'yi response'ta döndürür (yoksa yeni üretir).
      const metaEventId = (() => {
        try {
          if (typeof window !== "undefined" && window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
          }
        } catch (_) {}
        return `cr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      })();
      // fbp / fbc cookie'leri — Meta CAPI user_data için (hash'lenmez)
      const readCookie = (name) => {
        try {
          const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]+)"));
          return m ? decodeURIComponent(m[1]) : null;
        } catch (_) { return null; }
      };
      payload.meta_event_id = metaEventId;
      payload.meta_fbp = readCookie("_fbp");
      payload.meta_fbc = readCookie("_fbc");
      payload.meta_user_agent = (typeof navigator !== "undefined" && navigator.userAgent) || null;
      payload.meta_event_source_url = (typeof window !== "undefined" && window.location?.href) || null;
      // Kayıt isteğini doğrudan atıyoruz; AuthContext.register() ``checkAuth()``'u
      // çağırıp ``setUser`` yaptığında Login.jsx'teki ``useEffect`` redirect'i
      // tetikleniyor ve bazen bu, aşağıdaki fbq/gtag çağrılarından ÖNCE Login
      // bileşenini unmount edebiliyor → browser Pixel event'i kaybolabiliyor.
      // Bu nedenle önce response'u alıp fbq+gtag'i ateşliyoruz; AuthContext
      // state güncellemesi ve redirect bundan SONRA gerçekleşir.
      const { data: regResp } = await api.post("/auth/register", payload);
      // Başarılı kayıt sonrası UTM cache'ini temizle (bir sonraki
      // kullanıcının kaydını etkilemesin).
      try { window.localStorage.removeItem("penz_register_utm"); } catch (_) {}
      // Meta Pixel — sadece backend'den başarılı kayıt sonrası, kullanıcı başına tek event.
      // Server CAPI event ile aynı eventID geçilerek Meta'da deduplication sağlanır.
      const sharedEventId = (regResp && regResp.meta_event_id) || metaEventId;
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        try {
          window.fbq("track", "CompleteRegistration", {}, { eventID: sharedEventId });
        } catch (_) { /* analytics kayıt akışını bloklamamalı */ }
      }
      // Google Ads — aynı başarı noktasında, kullanıcı başına tek dönüşüm
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        try {
          window.gtag("event", "conversion", {
            send_to: "AW-18411854684/cKGXCLbSn-gcENy2ustE",
            value: 1.0,
            currency: "TRY",
          });
        } catch (_) {
          // Analytics hatası kayıt akışını engellememeli
        }
      }
      // fbq/gtag ateşlendikten SONRA auth state'i güncelle → useEffect redirect
      // bu noktadan sonra çalışır, browser event'i kayıp etmez.
      if (regResp && regResp.access_token) {
        try { localStorage.setItem("auth_token", regResp.access_token); } catch (_) {}
      }
      // Yandex: registration_completed uygulamaya geçişte bir kez (MetricaTracker).
      markRegistrationCompleted();
      try { await checkAuth(); } catch (_) {}
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Kayıt başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative">
          <div className="flex items-center">
            <img
              src="/penz-logo.png"
              alt="PENZ — Simply Smart."
              className="h-14 w-auto object-contain brightness-0 invert"
              data-testid="login-brand-logo"
            />
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mt-3">Yönetim Paneli</div>
        </div>
        <div className="relative max-w-md">
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">Özel Sistem</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight leading-tight">
            İşletmenizin <span className="text-emerald-400">finansal kontrolü</span> tek bir panelde.
          </h1>
          <p className="text-white/60 mt-4 text-sm leading-relaxed">
            Müşteri, proje, gelir, gider, kasa, alacak ve borç takibi. Türk Lirası ile, gün gün, ay ay.
          </p>
          <div className="mt-8 flex items-center gap-2 text-xs text-white/60">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span>Tek kullanıcılı korumalı erişim</span>
          </div>
        </div>
        <div className="relative text-xs text-white/40">© {new Date().getFullYear()} • Özel Kullanım</div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center">
            <img
              src="/penz-logo.png"
              alt="PENZ — Simply Smart."
              className="h-10 w-auto object-contain"
              data-testid="login-mobile-brand-logo"
            />
          </div>

          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
            {mode === "login" ? "Giriş" : "Firma Oluştur"}
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 mt-1">
            {mode === "login" ? "Hoş geldiniz" : "Yeni firma kaydı"}
          </h2>
          <p className="text-sm text-slate-500 mt-1.5">
            {mode === "login"
              ? "Devam etmek için bilgilerinizi girin."
              : "Firmanızı ve sahip hesabınızı oluşturun."}
          </p>

          {mode === "login" ? (
            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-xs font-medium text-slate-700">E-posta veya kullanıcı adı</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-9 h-11 rounded-lg border-slate-200 focus-visible:ring-slate-900/15 focus-visible:border-slate-900"
                    placeholder="E-posta veya kullanıcı adı"
                    required
                    data-testid="login-username-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-medium text-slate-700">Şifre</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 h-11 rounded-lg border-slate-200 focus-visible:ring-slate-900/15 focus-visible:border-slate-900"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              {error && (
                <div className="text-xs px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-100" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium"
                data-testid="login-submit-button"
              >
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </Button>

              <div className="text-center">
                <a
                  href="/sifremi-unuttum"
                  className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  Şifremi Unuttum
                </a>
              </div>

              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-[11px] uppercase tracking-[0.14em] text-slate-400">
                    veya
                  </span>
                </div>
              </div>

              <Button
                type="button"
                onClick={() => switchMode("register")}
                variant="outline"
                className="w-full h-11 rounded-lg bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 font-medium"
                data-testid="switch-to-register-button"
              >
                Yeni Firma Oluştur
              </Button>
            </form>
          ) : (
            <form onSubmit={onRegisterSubmit} className="mt-8 space-y-3.5" data-testid="register-form">
              {/* Hesap türü seçimi — zorunlu, varsayılan yok */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Hesap Türü</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRegForm({ ...regForm, billing_type: "individual", company_kind: "" })}
                    className={
                      "h-16 rounded-lg border text-sm font-medium transition-colors flex flex-col items-center justify-center " +
                      (regForm.billing_type === "individual"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")
                    }
                    data-testid="register-billing-type-individual"
                  >
                    <div>Şahıs</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegForm({ ...regForm, billing_type: "company" })}
                    className={
                      "h-16 rounded-lg border text-sm font-medium transition-colors flex flex-col items-center justify-center px-2 " +
                      (regForm.billing_type === "company"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")
                    }
                    data-testid="register-billing-type-company"
                  >
                    <div>Şirket</div>
                  </button>
                </div>
              </div>

              {/* Şahıs alanları */}
              {regForm.billing_type === "individual" && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <RegField label="Ad" value={regForm.first_name}
                      onChange={(v) => setRegForm({ ...regForm, first_name: v })}
                      placeholder="Adınız" required
                      testId="register-first-name-input" />
                    <RegField label="Soyad" value={regForm.last_name}
                      onChange={(v) => setRegForm({ ...regForm, last_name: v })}
                      placeholder="Soyadınız" required
                      testId="register-last-name-input" />
                  </div>
                  <RegField label="T.C. Kimlik Numarası" icon={IdCard} value={regForm.national_id}
                    onChange={(v) => setRegForm({ ...regForm, national_id: v.replace(/\D/g, "").slice(0, 11) })}
                    placeholder="11 haneli" required inputMode="numeric" maxLength={11}
                    testId="register-national-id-input" />
                </>
              )}

              {/* Şirket alt türü seçimi — sade dropdown */}
              {regForm.billing_type === "company" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-700">Şirket Türü</Label>
                  <select
                    value={regForm.company_kind}
                    onChange={(e) => setRegForm({ ...regForm, company_kind: e.target.value })}
                    required
                    className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 disabled:opacity-60"
                    data-testid="register-company-kind-select"
                  >
                    <option value="">Şirket türünü seçin</option>
                    <option value="sahis_sirketi">Şahıs Şirketi</option>
                    <option value="limited">Limited Şirket</option>
                    <option value="anonim">Anonim Şirket</option>
                  </select>
                </div>
              )}

              {/* Şirket alanları — company_kind seçildikten sonra göster */}
              {regForm.billing_type === "company" && regForm.company_kind && (
                <>
                  <div>
                    <RegField label="Firma / Ticari Ünvan" icon={Building2} value={regForm.legal_name}
                      onChange={(v) => setRegForm({ ...regForm, legal_name: v })}
                      placeholder="Örn: ABC Danışmanlık A.Ş." required
                      testId="register-legal-name-input" />
                    <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                      Faturada kullanılacak resmi ünvanı eksiksiz yazın. Şahıs şirketiyseniz resmi ünvanınızı veya ad soyadınızı yazın.
                    </p>
                  </div>
                  <RegField label="Yetkili Ad Soyad" icon={IdCard} value={regForm.full_name}
                    onChange={(v) => setRegForm({ ...regForm, full_name: v })}
                    placeholder="Yetkili kişinin adı" required
                    testId="register-authorized-person-input" />
                  {regForm.company_kind === "sahis_sirketi" ? (
                    <RegField label="T.C. Kimlik Numarası" icon={IdCard} value={regForm.national_id}
                      onChange={(v) => setRegForm({ ...regForm, national_id: v.replace(/\D/g, "").slice(0, 11) })}
                      placeholder="11 haneli" required inputMode="numeric" maxLength={11}
                      testId="register-national-id-input" />
                  ) : (
                    <RegField label="Vergi Numarası" icon={IdCard} value={regForm.tax_number}
                      onChange={(v) => setRegForm({ ...regForm, tax_number: v.replace(/\D/g, "").slice(0, 10) })}
                      placeholder="10 haneli" required inputMode="numeric" maxLength={10}
                      testId="register-tax-number-input" />
                  )}
                  <RegField label="Vergi Dairesi" icon={Building2} value={regForm.tax_office}
                    onChange={(v) => setRegForm({ ...regForm, tax_office: v })}
                    placeholder="Örn: Beşiktaş" required
                    testId="register-tax-office-input" />
                  <RegField label="Fatura Adresi" icon={Building2} value={regForm.billing_address}
                    onChange={(v) => setRegForm({ ...regForm, billing_address: v })}
                    placeholder="Mahalle, cadde, no" required
                    testId="register-billing-address-input" />
                  <div className="grid grid-cols-2 gap-2">
                    <RegField label="İl" value={regForm.billing_city}
                      onChange={(v) => setRegForm({ ...regForm, billing_city: v })}
                      placeholder="İstanbul" required
                      testId="register-billing-city-input" />
                    <RegField label="İlçe" value={regForm.billing_district}
                      onChange={(v) => setRegForm({ ...regForm, billing_district: v })}
                      placeholder="Kadıköy" required
                      testId="register-billing-district-input" />
                  </div>
                </>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Telefon Numarası</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={regForm.phone}
                    onChange={(e) => setRegForm({ ...regForm, phone: e.target.value.replace(/[^0-9\s]/g, "") })}
                    className="pl-9 h-11 rounded-lg border-slate-200"
                    placeholder="05xx xxx xx xx"
                    required
                    minLength={10}
                    maxLength={20}
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">E-posta</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    autoComplete="email"
                    value={regForm.email}
                    onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    className="pl-9 h-11 rounded-lg border-slate-200"
                    placeholder="ornek@firma.com"
                    required
                    data-testid="register-email-input"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  {regForm.billing_type === "company"
                    ? "Bu e-posta adresi giriş yaparken kullanıcı adı olarak kullanılacaktır. Lütfen doğru yazın."
                    : "Lütfen e-posta adresinizi doğru yazın. Giriş yaparken kullanıcı adı olarak bu e-posta adresini kullanacaksınız."}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Şifre</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={regForm.password}
                    onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                    className="pl-9 h-11 rounded-lg border-slate-200"
                    placeholder="En az 8 karakter"
                    required
                    minLength={8}
                    data-testid="register-password-input"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  En az 8 karakter, bir harf ve bir rakam içermelidir.
                </p>
              </div>

              {error && (
                <div className="text-xs px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-100" data-testid="register-error">
                  {error}
                </div>
              )}

              <div className="pt-2 space-y-2.5">
                <label className="flex items-start gap-2.5 text-xs text-slate-700 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 accent-slate-900 shrink-0"
                    data-testid="register-terms-checkbox"
                  />
                  <span>
                    <button
                      type="button"
                      onClick={() => setOpenLegalModal("terms")}
                      className="text-slate-900 font-medium underline underline-offset-2 hover:text-slate-700"
                      data-testid="register-terms-link"
                    >
                      Kullanım Koşulları
                    </button>
                    &apos;nı okudum ve kabul ediyorum.
                  </span>
                </label>

                <label className="flex items-start gap-2.5 text-xs text-slate-700 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={kvkkAcknowledged}
                    onChange={(e) => setKvkkAcknowledged(e.target.checked)}
                    required
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 accent-slate-900 shrink-0"
                    data-testid="register-kvkk-checkbox"
                  />
                  <span>
                    <button
                      type="button"
                      onClick={() => setOpenLegalModal("kvkk")}
                      className="text-slate-900 font-medium underline underline-offset-2 hover:text-slate-700"
                      data-testid="register-kvkk-link"
                    >
                      KVKK Aydınlatma Metni
                    </button>
                    &apos;ni okudum ve bilgilendirildim.
                  </span>
                </label>

                <label className="flex items-start gap-2.5 text-xs text-slate-700 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 accent-slate-900 shrink-0"
                    data-testid="register-marketing-checkbox"
                  />
                  <span>
                    Penz’e ilişkin yeni özellik, duyuru, bilgilendirme, fırsat ve kampanyalardan e-posta, SMS, telefon araması ve WhatsApp yoluyla haberdar olmak istiyorum. İletişim tercihlerinizi istediğiniz zaman Penz hesabınızdaki Ayarlar bölümünden değiştirebilir veya iptal edebilirsiniz.
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading || !regForm.billing_type}
                className="w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium disabled:opacity-50"
                data-testid="register-submit-button"
              >
                {loading ? "Oluşturuluyor..." : "Firmayı Oluştur"}
              </Button>

              <div className="text-xs text-slate-500 text-center pt-2">
                Zaten hesabınız var mı?{" "}
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="text-slate-900 font-medium hover:underline"
                  data-testid="switch-to-login-button"
                >
                  Giriş Yap
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {openLegalModal && (
        <LegalModal
          title={openLegalModal === "terms" ? "Kullanım Koşulları" : "KVKK Aydınlatma Metni"}
          body={openLegalModal === "terms" ? TERMS_BODY : KVKK_BODY}
          onClose={() => setOpenLegalModal(null)}
        />
      )}
    </div>
  );
}

function LegalModal({ title, body, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="legal-modal"
    >
      <div
        className="relative w-full max-w-lg max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-base font-semibold text-slate-900" data-testid="legal-modal-title">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Kapat"
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            data-testid="legal-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto text-sm leading-relaxed text-slate-700 whitespace-pre-line" data-testid="legal-modal-body">
          {body}
        </div>
      </div>
    </div>
  );
}

function RegField({ label, icon: Icon, value, onChange, placeholder, required, inputMode, maxLength, testId, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-700">{label}</Label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />}
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={(Icon ? "pl-9 " : "") + "h-11 rounded-lg border-slate-200"}
          placeholder={placeholder}
          required={required}
          inputMode={inputMode}
          maxLength={maxLength}
          data-testid={testId}
        />
      </div>
    </div>
  );
}
